import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { isUnsupported, isKnownIssueActive, knownIssuesApi } from '../../lib/api';
import { errorMessage } from '../../lib/api-error';
import { t, useLocale } from '../../lib/i18n';
import type { KnownIssue } from '../../lib/types';
import { sx } from './styles';

// Hand-rolled like FlowsPanel rather than a <SettingsTable>: the generic CRUD
// table edits flat scalar fields in place, and this row is a paired EN/HE copy
// deck plus a window with two mutually-aware timestamps and a resolve action
// that is not a delete. Same sx tokens, so it still reads as one settings page.

interface Draft {
  /** Present when editing an existing notice. */
  id?: string;
  title: string;
  message: string;
  titleHe: string;
  messageHe: string;
  /** datetime-local value ('' = open-ended). */
  endsAt: string;
  /** The stored start, so an edited window is validated against the real one. */
  startsAt?: number;
}

const EMPTY_DRAFT: Draft = { title: '', message: '', titleHe: '', messageHe: '', endsAt: '' };

/** epoch ms -> the local-time string an <input type="datetime-local"> wants. */
function toLocalInput(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local (local tz) -> epoch ms. null for empty/unparseable. */
function fromLocalInput(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function fmt(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleString();
}

function toDraft(issue: KnownIssue): Draft {
  return {
    id: issue.id,
    title: issue.title,
    message: issue.message,
    titleHe: issue.titleHe ?? '',
    messageHe: issue.messageHe ?? '',
    endsAt: toLocalInput(issue.endsAt),
    startsAt: issue.startsAt,
  };
}

/**
 * Why a notice is not live, in the operator's terms. "Resolved" and "the window
 * closed on its own" are different facts about an incident and the row has to
 * tell them apart — the first is a decision someone made, the second is a timer
 * nobody watched.
 */
function statusOf(issue: KnownIssue, now: number): { key: string; color: string } {
  if (isKnownIssueActive(issue, now)) return { key: 'settings.knownIssues.statusLive', color: '#dc2626' };
  if (issue.active !== true) return { key: 'settings.knownIssues.statusResolved', color: '#6b7280' };
  if (Number(issue.startsAt) > now) return { key: 'settings.knownIssues.statusScheduled', color: '#2563eb' };
  return { key: 'settings.knownIssues.statusEnded', color: '#6b7280' };
}

/** A closed window cannot be re-opened by flipping `active` — the notice would
 *  come back "on" and still say nothing. Only offer Reactivate when it would
 *  actually do something. */
function canReactivate(issue: KnownIssue, now: number): boolean {
  if (issue.active === true) return false;
  return issue.endsAt === null || issue.endsAt === undefined || Number(issue.endsAt) > now;
}

export function KnownIssuesPanel({ canEdit }: { canEdit: boolean }) {
  const { claims } = useAuth();
  const tenantCode = claims?.tenantCode ?? '';
  useLocale();
  const [items, setItems] = useState<KnownIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [unsupported, setUnsupported] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  // Live/scheduled/ended is a function of the clock, so the table re-evaluates
  // on a timer instead of freezing at whatever was true when it loaded.
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    if (!tenantCode) return;
    setLoading(true);
    try {
      const r = await knownIssuesApi.list(tenantCode);
      if (isUnsupported(r)) {
        setUnsupported(true);
        setItems([]);
      } else {
        setUnsupported(false);
        setItems(r.issues ?? []);
      }
      setNow(Date.now());
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  }, [tenantCode]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const liveCount = useMemo(() => items.filter((i) => isKnownIssueActive(i, now)).length, [items, now]);

  function validate(d: Draft): string | null {
    if (!d.title.trim() || !d.message.trim()) return t('settings.knownIssues.required');
    const endsAt = fromLocalInput(d.endsAt);
    if (endsAt !== null) {
      const startsAt = d.startsAt ?? Date.now();
      if (endsAt <= startsAt) return t('settings.knownIssues.invalidWindow');
      if (endsAt <= Date.now()) return t('settings.knownIssues.pastWindow');
    }
    return null;
  }

  async function save() {
    if (!draft) return;
    const problem = validate(draft);
    if (problem) {
      setErr(problem);
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const body = {
        title: draft.title.trim(),
        message: draft.message.trim(),
        // Sent even when blank on an edit: that is how the operator CLEARS a
        // Hebrew variant they no longer want customers to hear.
        titleHe: draft.titleHe.trim(),
        messageHe: draft.messageHe.trim(),
        endsAt: fromLocalInput(draft.endsAt),
      };
      if (draft.id) await knownIssuesApi.update(tenantCode, draft.id, body);
      else await knownIssuesApi.create(tenantCode, body);
      setDraft(null);
      await load();
    } catch (e) {
      setErr(e);
    } finally {
      setSaving(false);
    }
  }

  async function resolve(issue: KnownIssue) {
    if (!window.confirm(t('settings.knownIssues.resolveConfirm', { title: issue.title || issue.titleHe || '' }))) {
      return;
    }
    setErr(null);
    try {
      await knownIssuesApi.update(tenantCode, issue.id, { resolve: true });
    } catch (e) {
      setErr(e);
    }
    await load();
  }

  async function reactivate(issue: KnownIssue) {
    setErr(null);
    try {
      await knownIssuesApi.update(tenantCode, issue.id, { active: true });
    } catch (e) {
      setErr(e);
    }
    await load();
  }

  return (
    <section>
      <h2 style={{ margin: 0, fontSize: 18, marginBottom: 4 }}>{t('settings.knownIssues.title')}</h2>
      <p style={{ ...sx.hint, marginTop: 0, marginBottom: 16, fontSize: 13 }}>
        {t('settings.knownIssues.intro')}
      </p>

      {unsupported && (
        <div style={{ ...sx.hint, marginBottom: 12 }}>{t('settings.knownIssues.backendMissing')}</div>
      )}
      {!canEdit && <div style={{ ...sx.hint, marginBottom: 12 }}>{t('settings.knownIssues.readOnly')}</div>}

      {liveCount > 0 && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 16,
            fontSize: 13,
            color: '#991b1b',
          }}
        >
          {t('settings.knownIssues.liveCount', { n: liveCount })}
        </div>
      )}

      {/* Resolve/reactivate fail outside the editor card, where `err` would
          otherwise be the only place an error is shown. */}
      {!draft && err !== null && (
        <p style={{ color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{errorMessage(err)}</p>
      )}

      {!draft && canEdit && !unsupported && (
        <button
          style={{ ...sx.btnPrimary, marginBottom: 16 }}
          onClick={() => {
            setErr(null);
            setDraft({ ...EMPTY_DRAFT });
          }}
        >
          {t('settings.knownIssues.new')}
        </button>
      )}

      {draft && (
        <div style={sx.card}>
          <label style={sx.label}>{t('settings.knownIssues.fieldTitle')}</label>
          <input
            style={sx.input}
            dir="auto"
            maxLength={120}
            value={draft.title}
            placeholder={t('settings.knownIssues.fieldTitlePlaceholder')}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />

          <div style={{ marginTop: 14 }}>
            <label style={sx.label}>{t('settings.knownIssues.fieldMessage')}</label>
            <textarea
              style={{ ...sx.input, minHeight: 68, resize: 'vertical' }}
              dir="auto"
              maxLength={600}
              value={draft.message}
              placeholder={t('settings.knownIssues.fieldMessagePlaceholder')}
              onChange={(e) => setDraft({ ...draft, message: e.target.value })}
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={sx.label}>{t('settings.knownIssues.fieldTitleHe')}</label>
            <input
              style={sx.input}
              dir="auto"
              maxLength={120}
              value={draft.titleHe}
              onChange={(e) => setDraft({ ...draft, titleHe: e.target.value })}
            />
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={sx.label}>{t('settings.knownIssues.fieldMessageHe')}</label>
            <textarea
              style={{ ...sx.input, minHeight: 68, resize: 'vertical' }}
              dir="auto"
              maxLength={600}
              value={draft.messageHe}
              onChange={(e) => setDraft({ ...draft, messageHe: e.target.value })}
            />
            <p style={sx.hint}>{t('settings.knownIssues.hebrewHint')}</p>
          </div>

          <div style={{ marginTop: 14 }}>
            <label style={sx.label}>{t('settings.knownIssues.fieldEndsAt')}</label>
            <input
              type="datetime-local"
              style={{ ...sx.input, maxWidth: 260 }}
              value={draft.endsAt}
              onChange={(e) => setDraft({ ...draft, endsAt: e.target.value })}
            />
            <p style={sx.hint}>{t('settings.knownIssues.endsAtHint')}</p>
          </div>

          {err !== null && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 10 }}>{errorMessage(err)}</p>}

          <div style={{ ...sx.row, marginTop: 16, marginBottom: 0 }}>
            <button style={sx.btnPrimary} disabled={saving} onClick={() => void save()}>
              {saving
                ? t('common.loading')
                : draft.id
                  ? t('settings.knownIssues.saveEdit')
                  : t('settings.knownIssues.save')}
            </button>
            <button
              style={sx.btnSecondary}
              disabled={saving}
              onClick={() => {
                setDraft(null);
                setErr(null);
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={sx.hint}>{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <p style={sx.hint}>{t('settings.knownIssues.empty')}</p>
      ) : (
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>{t('settings.knownIssues.colIssue')}</th>
              <th style={sx.th}>{t('settings.knownIssues.colWindow')}</th>
              <th style={sx.th}>{t('settings.knownIssues.colStatus')}</th>
              <th style={sx.th}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((issue) => {
              const live = isKnownIssueActive(issue, now);
              const status = statusOf(issue, now);
              return (
                <tr key={issue.id} style={live ? { background: '#fef2f2' } : undefined}>
                  <td style={{ ...sx.td, verticalAlign: 'top' }}>
                    <div dir="auto" style={{ fontWeight: 500 }}>
                      {issue.title || issue.titleHe}
                    </div>
                    <div dir="auto" style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {issue.message || issue.messageHe}
                    </div>
                    {(issue.titleHe || issue.messageHe) && (
                      <div dir="auto" style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        <span style={{ ...sx.badge('#7c3aed'), fontSize: 10, marginInlineEnd: 6 }}>HE</span>
                        {issue.titleHe}
                        {issue.titleHe && issue.messageHe ? ' — ' : ''}
                        {issue.messageHe}
                      </div>
                    )}
                  </td>
                  <td style={{ ...sx.td, color: '#6b7280', fontSize: 12, verticalAlign: 'top' }}>
                    {issue.endsAt === null || issue.endsAt === undefined
                      ? t('settings.knownIssues.windowOpen', { from: fmt(issue.startsAt) })
                      : t('settings.knownIssues.windowUntil', {
                          from: fmt(issue.startsAt),
                          to: fmt(issue.endsAt),
                        })}
                    {issue.resolvedAt ? (
                      <div style={{ marginTop: 2 }}>
                        {t('settings.knownIssues.statusResolved')} {fmt(issue.resolvedAt)}
                        {issue.resolvedBy ? ` · ${issue.resolvedBy}` : ''}
                      </div>
                    ) : null}
                  </td>
                  <td style={{ ...sx.td, verticalAlign: 'top' }}>
                    <span style={sx.badge(status.color)}>{t(status.key)}</span>
                  </td>
                  <td style={{ ...sx.td, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    {canEdit && (
                      <button
                        style={{ ...sx.btnSecondary, marginInlineEnd: 4 }}
                        onClick={() => {
                          setErr(null);
                          setDraft(toDraft(issue));
                        }}
                      >
                        {t('settings.knownIssues.edit')}
                      </button>
                    )}
                    {canEdit && issue.active === true && (
                      <button style={sx.btnDanger} onClick={() => void resolve(issue)}>
                        {t('settings.knownIssues.resolve')}
                      </button>
                    )}
                    {canEdit && canReactivate(issue, now) && (
                      <button style={sx.btnSecondary} onClick={() => void reactivate(issue)}>
                        {t('settings.knownIssues.reactivate')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
